import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button } from '@alwindows/shared'
import { useQuote } from '../quote/QuoteContext'
import { ROUTE_SELECT_SCREENS } from '../routes'
import { getConfigValue, lineDisplayName, type ScreenLine } from '../quote/types'
import { LineConfigurator } from './LineConfigurator'

/**
 * Deep-link route for configuring a single line outside the Select screens
 * list. That page configures lines inline (the configurator opens inside
 * the row's grey card); this page exists so bookmarks / direct URLs still
 * work, and so screen readers / older flows have a non-collapsing landing.
 *
 * The actual configurator UI lives in `LineConfigurator` so both surfaces
 * stay in sync.
 */
export function Edit() {
  const params = useParams<{ sono: string; itemNo: string }>()
  const navigate = useNavigate()
  const { quote, setQuote } = useQuote()

  const lineIndex = quote.lines.findIndex(
    (l) => l.sourceItem.SONo === params.sono && l.sourceItem.ItemNo === params.itemNo,
  )
  const line = lineIndex >= 0 ? quote.lines[lineIndex] : null

  if (!line) {
    return (
      <div className="al-edit">
        <h2>Screen not found</h2>
        <Button variant="secondary" asChild>
          <Link to={ROUTE_SELECT_SCREENS}>Back to select screens</Link>
        </Button>
      </div>
    )
  }

  const item = line.sourceItem
  const frameWid = getConfigValue(line.sourceConfig, 'FRAME_WID')
  const frameHt = getConfigValue(line.sourceConfig, 'FRAME_HT')
  const salesOrg = quote.customer?.salesOrg || item.SalesOrg
  // The home is one colour, seeded from the first item's FM_ALU_COLOUR Value
  // (legacy's `defaultFrame`). Pass it so this line's swatch pre-selects even
  // when SAP only carried the colour Value on the first item.
  const homeColourName = getConfigValue(quote.lines[0]?.sourceConfig ?? [], 'FM_ALU_COLOUR')

  const commit = (next: ScreenLine) => {
    setQuote((prev) => ({
      ...prev,
      // Configuring a line means the customer wants it on the order.
      lines: prev.lines.map((l, i) => (i === lineIndex ? { ...next, selected: true } : l)),
    }))
    navigate(ROUTE_SELECT_SCREENS)
  }

  return (
    <div className="al-edit">
      <header className="al-edit__header">
        <div className="al-edit__header-text">
          <p className="al-edit__eyebrow xxsmall uppercase mid-grey no-m">
            BAL {line.balRating || 'LOW'}
            {item.Material ? ` · ${item.Material}` : ''}
          </p>
          <h2 className="large bold no-m-t m-bx1">{lineDisplayName(line)}</h2>
          {frameWid && frameHt ? (
            <p className="xsmall mid-grey no-m">
              {frameWid} × {frameHt}
            </p>
          ) : null}
        </div>
      </header>

      <LineConfigurator
        line={line}
        salesOrg={salesOrg}
        state={quote.customer?.state ?? ''}
        homeColourName={homeColourName}
        onSave={commit}
        onCancel={() => navigate(ROUTE_SELECT_SCREENS)}
      />
    </div>
  )
}
